import { useState, useRef, useEffect } from "react";
import axios from "axios";
import "./Debate.css";

interface DebateMessage {
  speaker: string;
  content: string;
}

const getApi = (path: string) => {
  return window.location.hostname === "localhost"
    ? `http://localhost/chat/debate/${path}`
    : `/chat/debate/${path}`;
};

function Debate() {
  const [topic, setTopic] = useState("");
  const [debateId, setDebateId] = useState("");
  const [messages, setMessages] = useState<DebateMessage[]>([]);
  const [typing, setTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [moderator, setModerator] = useState("");
  const [debaters, setDebaters] = useState<string[]>([]);
  const [timeLeft, setTimeLeft] = useState(60);
  const [finished, setFinished] = useState(false);
  const [typingDots, setTypingDots] = useState(".");
  
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  /* 🔥 작성중 점 애니메이션 */
  useEffect(() => {
    if (!typing) return;

    const interval = setInterval(() => {
      setTypingDots(prev => (prev.length >= 3 ? "." : prev + "."));
    }, 400);

    return () => clearInterval(interval);
  }, [typing]);

  /* 🔥 전체 페이지 스크롤 자동 이동 */
  useEffect(() => {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "smooth"
    });
  }, [messages, typing]);

  /* 🔥 60초 타이머 */
  useEffect(() => {
    if (!debateId || finished) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setFinished(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [debateId, finished]);

  const startDebate = async () => {
    if (!topic.trim()) return;

    setMessages([]);
    setModerator("");
    setDebaters([]);
    setFinished(false);
    setTimeLeft(60);
    setLoading(true);

    const init = await axios.post(getApi("init"), { topic });

    setModerator(init.data.moderator);
    setDebaters(init.data.debaters);
    setDebateId(init.data.debateId);

    setLoading(false);
    runNext(init.data.debateId);
  };

  const runNext = async (id: string) => {
    if (finished) return;

    setTyping(true);

    const res = await axios.post(getApi("next"), { debateId: id });

    if (res.data.finished) {
      setFinished(true);
      setTyping(false);
      return;
    }

    setMessages(prev => [
      ...prev,
      { speaker: res.data.speaker, content: res.data.content }
    ]);

    setTyping(false);

    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      runNext(id);
    });
  };

  const getSpeakerColor = (speaker: string) => {
    switch (speaker) {
      case "GPT": return "#10a37f";
      case "Gemini": return "#4285F4";
      case "Claude": return "#d97706";
      case "Grok": return "#e11d48";
      default: return "#444";
    }
  };

  const getBubbleClass = (speaker: string) => {
    return `bubble ${speaker.toLowerCase()}`;
  };

  const timePercent = (timeLeft / 60) * 100;

  return (
    <div className="debate-container" ref={containerRef}>

      {/* 🔥 상단 */}
      <div className="top-bar">
        <div className="title">AI토론</div>

        {debateId && (
          <div className="timer-inline">
            <div className="timer-track">
              <div
                className="timer-fill"
                style={{
                  width: `${timePercent}%`,
                  background: timeLeft < 10 ? "#ef4444" : "#22c55e"
                }}
              />
            </div>
            <span>{timeLeft}s</span>
          </div>
        )}
      </div>

      {/* 🔥 입력 */}
      <div className="input-row">
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="토론 주제를 입력하세요..."
          onKeyDown={(e) => e.key === "Enter" && startDebate()}
        />
        <button onClick={startDebate} disabled={loading}>
          {loading ? "..." : "START"}
        </button>
      </div>

      {moderator && (
        <div className="role-row">
          🎙 {moderator} &nbsp;&nbsp; 🥊 {debaters.join(" / ")}
        </div>
      )}

      {/* 🔥 채팅 */}
      <div className="chat-area">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`message-row ${idx % 2 === 0 ? "left" : "right"}`}
          >
            <div
              className="speaker-name"
              style={{ color: getSpeakerColor(msg.speaker) }}
            >
              {msg.speaker}
            </div>

            <div className={getBubbleClass(msg.speaker)}>
              {msg.content}
            </div>
          </div>
        ))}

        {typing && !finished && (
          <div className="typing-fixed">
            작성중{typingDots}
          </div>
        )}

        {finished && (
          <div className="finished-banner">
            🔥 Debate Finished
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* 🔥 푸터 */}
      <div className="footer">
        © 2026 backend-lab.com · AI Debate Arena
      </div>

    </div>
  );
}

export default Debate;
